import { useCallback, useEffect, useState } from 'react';
import { CaretDown, CaretRight, Package, PencilSimple, Truck, X } from 'shared/icons';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { Id, Mono, Sku } from '../ui/Mono';
import { Skeleton } from '../ui/Skeleton';
import BlockTower from './collector/BlockTower';
import { LpnStatusBadgeVariant } from './LpnDetailPanel';
import { FormatBayWidth, FormatWeightKg, OccupancyBadgeVariant, StatusBadgeVariant } from './locationFields';
import { IsVirtualLocation } from './locationsTree';
import { ExtractListRows, NormalizeRack } from './payload';
import { FormatLpnProductCount } from './warehouseLabels';

/**
 * DRAWER PERMANENTE "O que tem aqui / O que está chegando" (r4).
 *
 * Não é mais overlay: é a COLUNA FIXA à direita da página de Localizações —
 * sempre visível, com estado vazio ("clique numa posição…") até alguém clicar
 * numa posição na Tabela, na Cascata ou no Mapa. "O que tem aqui" passou a ser
 * REAL no M3: são os LPNs da posição (GET /warehouse/lpns?location_id=). A
 * chegada (OSs em rota) segue com o empty-state honesto até o M5.
 *
 * `target`: null
 *   | {kind:'location', location}            (linha/posição/fixa clicada)
 *   | {kind:'block', rack, bay}              (bloco clicado no mapa — rack = LETRA, bay = nº do bloco)
 *   | {kind:'rack', rack}                    (porta-pallet inteiro clicado no mapa)
 *   | {kind:'code', code}                    (código sem linha carregada)
 * `onClear` limpa a seleção (volta ao estado vazio); `onShowOnMap(target)`
 * troca a página para o modo Mapa com a seleção correspondente; `onEdit(row)`
 * abre a edição da localização.
 */

const toFinite = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

function sortBlockRows(a, b) {
    const levelDiff = (toFinite(a.level) ?? 0) - (toFinite(b.level) ?? 0);
    return levelDiff !== 0 ? levelDiff : (toFinite(a.position) ?? 0) - (toFinite(b.position) ?? 0);
}

const PANEL_CLASS =
    'flex h-full min-h-[420px] flex-col rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950';

const SectionEmpty = ({ icon: IconComponent, title, message }) => (
    <section>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
            <IconComponent size={16} className="text-mono-500" />
            {title}
        </h3>
        <p className="rounded-lg border border-dashed border-mono-300 px-3 py-4 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
            {message}
        </p>
    </section>
);

const DetailRow = ({ term, children }) => (
    <div className="flex items-baseline justify-between gap-3 py-1">
        <dt className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">{term}</dt>
        <dd className="text-right text-sm text-mono-700 dark:text-mono-300">{children}</dd>
    </div>
);

const RowButton = ({ row, label, onSelect }) => (
    <button
        type="button"
        onClick={() => onSelect?.(row)}
        className="flex w-full flex-wrap items-center gap-2 rounded-lg px-1 py-2 text-left transition-colors hover:bg-mono-50 dark:hover:bg-mono-900"
    >
        <Id value={row.code} className="text-xs" />
        <span className="ml-auto flex items-center gap-1.5">
            <Badge variant={StatusBadgeVariant(row.status)}>{label(`wms.location_status.${row.status}`)}</Badge>
            <Badge variant={OccupancyBadgeVariant(row.occupancy || 'FREE')}>
                {label(`wms.occupancy.${row.occupancy || 'FREE'}`)}
            </Badge>
        </span>
    </button>
);

// Teto da lista de LPNs da posição: uma posição com mais que isso é anomalia
// de dado, não caso de uso — e o drawer não é a tela de auditoria.
const LPNS_PER_LOCATION_LIMIT = 100;

const LocationInfoPanel = ({ target, label, onClear, onSelectLocation, onShowOnMap, onEdit }) => {
    const [rackRows, setRackRows] = useState([]);
    const [rackLoading, setRackLoading] = useState(false);
    const [rackError, setRackError] = useState(null);
    const [lpnRows, setLpnRows] = useState([]);
    const [lpnLoading, setLpnLoading] = useState(false);
    const [lpnError, setLpnError] = useState(null);
    // Conteúdo do pallet aberto na lista. Quem está olhando a posição quer ver o
    // QUE tem dentro sem trocar de tela: a linha do LPN abre ali mesmo. O GET é
    // sob demanda (a listagem da posição só traz a contagem) e fica guardado por
    // código — reabrir o mesmo pallet não repete a chamada.
    const [openLpnCode, setOpenLpnCode] = useState(null);
    const [lpnDetails, setLpnDetails] = useState({});
    const [detailLoadingCode, setDetailLoadingCode] = useState(null);
    const [detailError, setDetailError] = useState(null);

    const toggleLpnItems = useCallback(
        async (code) => {
            if (!code) {
                return;
            }
            if (openLpnCode === code) {
                setOpenLpnCode(null);
                return;
            }
            setOpenLpnCode(code);
            setDetailError(null);
            if (lpnDetails[code]) {
                return;
            }
            setDetailLoadingCode(code);
            try {
                const data = await api.getWarehouseLpn(code);
                setLpnDetails((previous) => ({ ...previous, [code]: data || null }));
            } catch (error) {
                setDetailError(error?.message || label('wms.common.load_error'));
            } finally {
                setDetailLoadingCode(null);
            }
        },
        [openLpnCode, lpnDetails, label],
    );

    // Posições do rack/bloco buscadas FRESCAS (a listagem da página pode estar
    // filtrada por status/tipo — o drawer mostra o recorte inteiro, sempre).
    // `rack` é a LETRA do porta-pallet: string, nunca passa por Number().
    const fetchRack = target?.kind === 'block' || target?.kind === 'rack' ? NormalizeRack(target.rack) : null;
    useEffect(() => {
        if (fetchRack === null) {
            setRackRows([]);
            setRackError(null);
            setRackLoading(false);
            return undefined;
        }
        let alive = true;
        setRackLoading(true);
        setRackError(null);
        setRackRows([]);
        api.getWarehouseLocations({ rack: fetchRack, limit: 5000 })
            .then((data) => {
                if (alive) {
                    setRackRows(ExtractListRows(data, ['locations']));
                }
            })
            .catch((error) => {
                if (alive) {
                    setRackError(error?.message || label('wms.common.load_error'));
                }
            })
            .finally(() => {
                if (alive) {
                    setRackLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [fetchRack, label]);

    // LPNs da POSIÇÃO (M3): "o que tem aqui" deixou de ser promessa. Só existe
    // para posição — bloco/rack agrupam várias, e a resposta útil ali é escolher
    // a posição.
    const fetchLocationId = target?.kind === 'location' ? (target.location?.id ?? null) : null;
    useEffect(() => {
        // Trocou de posição: fecha o pallet que estava aberto. Sem isso a
        // expansão da posição anterior reaparece sobre a nova lista.
        setOpenLpnCode(null);
        setDetailError(null);
        if (fetchLocationId === null || fetchLocationId === undefined) {
            setLpnRows([]);
            setLpnError(null);
            setLpnLoading(false);
            return undefined;
        }
        let alive = true;
        setLpnLoading(true);
        setLpnError(null);
        setLpnRows([]);
        api.getWarehouseLpns({ location_id: fetchLocationId, limit: LPNS_PER_LOCATION_LIMIT, offset: 0 })
            .then((data) => {
                if (alive) {
                    setLpnRows(ExtractListRows(data, ['lpns']));
                }
            })
            .catch((error) => {
                if (alive) {
                    setLpnError(error?.message || label('wms.common.load_error'));
                }
            })
            .finally(() => {
                if (alive) {
                    setLpnLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [fetchLocationId, label]);

    // ── Estado vazio (drawer é permanente — nunca desmonta) ─────────────────
    if (!target) {
        return (
            <aside aria-label={label('wms.panel.empty_title')} className={PANEL_CLASS}>
                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
                    <Package size={30} className="text-mono-400" aria-hidden="true" />
                    <p className="text-sm font-medium text-black dark:text-white">
                        {label('wms.panel.empty_title')}
                    </p>
                    <p className="max-w-[280px] text-sm text-mono-500 dark:text-mono-400">
                        {label('wms.panel.empty_hint')}
                    </p>
                </div>
            </aside>
        );
    }

    const location = target.kind === 'location' ? target.location : null;
    const isVirtual = location ? IsVirtualLocation(location) : false;
    const isBlock = target.kind === 'block';
    const isRack = target.kind === 'rack';
    const isCode = target.kind === 'code';

    // Fala do dono: "Rack A · Bloco 01" — nunca rua, nunca lado.
    const rackTitle = isBlock || isRack ? `${label('wms.field.rack')} ${target.rack}` : null;
    const title = location
        ? location.code
        : isBlock
          ? `${rackTitle} · ${label('wms.field.bay')} ${String(target.bay).padStart(2, '0')}`
          : isRack
            ? rackTitle
            : target.code;

    const blockRows = isBlock
        ? rackRows.filter((row) => toFinite(row?.bay) === toFinite(target.bay)).sort(sortBlockRows)
        : [];
    const blockBayWidth = isBlock ? FormatBayWidth(blockRows.find((row) => row.bay_width)?.bay_width) : null;

    // A torre desenha um andar por linha, então só sabe representar posição COM
    // andar. Com ela clicável a lista embaixo virou uma segunda cópia do mesmo
    // bloco — sai. Mas o que a torre não consegue desenhar não pode sumir da
    // tela junto: essas poucas linhas continuam listadas.
    const towerRows = blockRows.filter((row) => toFinite(row?.level) !== null);
    const offTowerRows = blockRows.filter((row) => toFinite(row?.level) === null);

    const rackBays = isRack ? new Set(rackRows.map((row) => toFinite(row?.bay)).filter((bay) => bay !== null)).size : 0;
    // Linhas do rack sem nº de bloco: a cabeceira (STG-A-00) e afins.
    const rackStagings = isRack ? rackRows.filter((row) => toFinite(row?.bay) === null) : [];

    const canShowOnMap = Boolean(onShowOnMap) && !isVirtual && !isCode;
    // Exclusão de rack/posição SAIU da lista: agora só acontece dentro do
    // "Editar galpão" (modo edição do mapa, já permissionado).
    const showActionBar = canShowOnMap || (location && onEdit);

    return (
        <aside aria-label={title} className={PANEL_CLASS}>
            <header className="flex items-start justify-between gap-3 border-b border-mono-200 p-4 dark:border-mono-800">
                <div className="min-w-0 space-y-1.5">
                    <Mono className="block truncate text-lg font-semibold text-black dark:text-white" title={title}>
                        {title}
                    </Mono>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {location ? (
                            <>
                                <Badge>{label(`wms.location_type.${location.type}`)}</Badge>
                                <Badge variant={StatusBadgeVariant(location.status)}>
                                    {label(`wms.location_status.${location.status}`)}
                                </Badge>
                                <Badge variant={OccupancyBadgeVariant(location.occupancy || 'FREE')}>
                                    {label(`wms.occupancy.${location.occupancy || 'FREE'}`)}
                                </Badge>
                                {isVirtual ? (
                                    <Badge variant="neutral">{label('wms.locations.virtual_off_map')}</Badge>
                                ) : null}
                            </>
                        ) : null}
                        {isBlock && blockBayWidth ? (
                            <Badge variant="neutral">{`${label('wms.cascade.bay')} ${blockBayWidth} m`}</Badge>
                        ) : null}
                        {isRack ? <Badge variant="neutral">{label('wms.panel.selected_rack')}</Badge> : null}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    aria-label={label('wms.panel.clear')}
                    title={label('wms.panel.clear')}
                    className="shrink-0 rounded-lg p-2 transition-colors hover:bg-mono-100 dark:hover:bg-mono-900"
                >
                    <X size={18} className="text-mono-600 dark:text-mono-400" />
                </button>
            </header>

            {showActionBar ? (
                <div className="flex flex-wrap items-center gap-2 border-b border-mono-200 px-4 py-2.5 dark:border-mono-800">
                    {canShowOnMap ? (
                        <Button variant="secondary" size="sm" onClick={() => onShowOnMap(target)}>
                            {label('wms.locations.see_on_map')}
                        </Button>
                    ) : null}
                    {location && onEdit ? (
                        <Button variant="ghost" size="sm" onClick={() => onEdit(location)}>
                            <PencilSimple size={14} className="mr-1.5" />
                            {label('wms.common.edit')}
                        </Button>
                    ) : null}
                </div>
            ) : null}

            <div className="flex-1 space-y-5 overflow-y-auto p-4">
                {isCode ? (
                    <p className="rounded-lg border border-dashed border-mono-300 px-3 py-4 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
                        {label('wms.panel.code_not_loaded')}
                    </p>
                ) : null}

                {location ? (
                    <section>
                        <h3 className="mb-1 text-sm font-semibold text-black dark:text-white">
                            {label('wms.panel.details')}
                        </h3>
                        <dl className="divide-y divide-mono-100 dark:divide-mono-900">
                            {/* Rack é LETRA (string): mostra como veio, sem passar por número. */}
                            {location.rack ? (
                                <DetailRow term={label('wms.field.rack')}>
                                    <Mono>{String(location.rack)}</Mono>
                                </DetailRow>
                            ) : null}
                            {toFinite(location.bay) !== null ? (
                                <DetailRow term={label('wms.field.bay')}>
                                    <Mono>{String(location.bay)}</Mono>
                                </DetailRow>
                            ) : null}
                            {toFinite(location.level) !== null ? (
                                <DetailRow term={label('wms.field.level')}>
                                    <Mono>{String(location.level)}</Mono>
                                </DetailRow>
                            ) : null}
                            {toFinite(location.position) !== null ? (
                                <DetailRow term={label('wms.field.position')}>
                                    <Mono>{String(location.position)}</Mono>
                                </DetailRow>
                            ) : null}
                            {FormatBayWidth(location.bay_width) ? (
                                <DetailRow term={label('wms.field.bay_width')}>
                                    <Mono>{`${FormatBayWidth(location.bay_width)} m`}</Mono>
                                </DetailRow>
                            ) : null}
                            {FormatWeightKg(location.max_weight_kg) ? (
                                <DetailRow term={label('wms.field.max_weight_kg')}>
                                    <Mono>{FormatWeightKg(location.max_weight_kg)}</Mono>
                                </DetailRow>
                            ) : null}
                            {location.sector ? (
                                <DetailRow term={label('wms.field.sector')}>{location.sector}</DetailRow>
                            ) : null}
                            {location.notes ? (
                                <DetailRow term={label('wms.field.notes')}>{location.notes}</DetailRow>
                            ) : null}
                        </dl>
                    </section>
                ) : null}

                {location ? (
                    // "O que tem aqui" = os LPNs REAIS da posição (M3).
                    <section>
                        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
                            <Package size={16} className="text-mono-500" />
                            {label('wms.panel.whats_here')}
                            {/* A posição comporta UM pallet (§6.1: destino SINGLE já
                                ocupado é 409), então "(1)" é ruído — o esperado não
                                se anuncia. Mais de um só existe como dado legado
                                (§6.2) e aí o número precisa gritar: é anomalia a
                                resolver, não contagem. */}
                            {lpnRows.length > 1 ? (
                                <Badge variant="warning">
                                    {`${FormatInt(lpnRows.length)} ${label('wms.panel.lpns_conflict')}`}
                                </Badge>
                            ) : null}
                        </h3>
                        {lpnLoading ? (
                            <Skeleton className="h-16 w-full" />
                        ) : lpnError ? (
                            <p className="text-sm text-danger-600 dark:text-danger-400">{lpnError}</p>
                        ) : lpnRows.length === 0 ? (
                            <p className="rounded-lg border border-dashed border-mono-300 px-3 py-4 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
                                {label('wms.panel.no_lpns_here')}
                            </p>
                        ) : (
                            <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                {lpnRows.map((row) => {
                                    const isOpen = openLpnCode === row.code;
                                    const detail = lpnDetails[row.code];
                                    const detailItems = Array.isArray(detail?.items) ? detail.items : [];
                                    return (
                                        <li key={row.id || row.code}>
                                            <button
                                                type="button"
                                                onClick={() => toggleLpnItems(row.code)}
                                                aria-expanded={isOpen}
                                                className="flex w-full items-center gap-2 rounded-lg px-1 py-2 text-left transition-colors hover:bg-mono-50 dark:hover:bg-mono-900"
                                            >
                                                {isOpen ? (
                                                    <CaretDown size={12} className="shrink-0 text-mono-500" />
                                                ) : (
                                                    <CaretRight size={12} className="shrink-0 text-mono-500" />
                                                )}
                                                <Id value={row.code} className="text-xs font-medium" />
                                                <span className="ml-auto flex items-center gap-1.5">
                                                    <span className="text-xs text-mono-500 dark:text-mono-400">
                                                        {FormatLpnProductCount(row.item_count, label)}
                                                    </span>
                                                    <Badge variant={LpnStatusBadgeVariant(row.status)}>
                                                        {label(`wms.lpn_status.${row.status}`)}
                                                    </Badge>
                                                </span>
                                            </button>
                                            {isOpen ? (
                                                <div className="pb-2 pl-5">
                                                    {detailLoadingCode === row.code ? (
                                                        <Skeleton className="h-12 w-full" />
                                                    ) : detailError ? (
                                                        <p className="text-sm text-danger-600 dark:text-danger-400">
                                                            {detailError}
                                                        </p>
                                                    ) : detailItems.length === 0 ? (
                                                        <p className="text-xs text-mono-500 dark:text-mono-400">
                                                            {label('wms.lpns.items_empty')}
                                                        </p>
                                                    ) : (
                                                        <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                                            {detailItems.map((item) => (
                                                                <li
                                                                    key={
                                                                        item.id ||
                                                                        `${item.product_id}-${item.supplier_lot || ''}`
                                                                    }
                                                                    className="flex items-start gap-2 py-1.5"
                                                                >
                                                                    <span className="min-w-0 flex-1">
                                                                        <Sku
                                                                            value={item.sku}
                                                                            className="text-xs font-medium"
                                                                        />
                                                                        {item.name ? (
                                                                            <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                                                                {item.name}
                                                                            </span>
                                                                        ) : null}
                                                                        {item.supplier_lot ? (
                                                                            <span className="block text-[11px] text-mono-500 dark:text-mono-400">
                                                                                {`${label('wms.lpn.field.supplier_lot')}: `}
                                                                                <Mono>{item.supplier_lot}</Mono>
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                    <Mono className="shrink-0 text-sm font-semibold text-black dark:text-white">
                                                                        {item.qty}
                                                                    </Mono>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                ) : null}

                {isBlock ? (
                    <SectionEmpty
                        icon={Package}
                        title={label('wms.panel.whats_here')}
                        message={label('wms.panel.whats_here_block')}
                    />
                ) : null}

                {!isCode && !isRack ? (
                    <SectionEmpty
                        icon={Truck}
                        title={label('wms.panel.incoming')}
                        message={label('wms.panel.incoming_empty')}
                    />
                ) : null}

                {isRack ? (
                    <section>
                        <h3 className="mb-2 text-sm font-semibold text-black dark:text-white">
                            {label('wms.panel.rack_summary')}
                        </h3>
                        {rackLoading ? (
                            <Skeleton className="h-24 w-full" />
                        ) : rackError ? (
                            <p className="text-sm text-danger-600 dark:text-danger-400">{rackError}</p>
                        ) : (
                            <>
                                <dl className="divide-y divide-mono-100 dark:divide-mono-900">
                                    <DetailRow term={label('wms.cascade.positions_suffix')}>
                                        <Mono>{String(rackRows.length)}</Mono>
                                    </DetailRow>
                                    <DetailRow term={label('wms.field.bay')}>
                                        <Mono>{String(rackBays)}</Mono>
                                    </DetailRow>
                                </dl>
                                {rackStagings.length > 0 ? (
                                    <ul className="mt-2 divide-y divide-mono-100 dark:divide-mono-900">
                                        {rackStagings.map((row) => (
                                            <li key={row.id || row.code}>
                                                <RowButton row={row} label={label} onSelect={onSelectLocation} />
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </>
                        )}
                    </section>
                ) : null}

                {isBlock ? (
                    <section>
                        <h3 className="mb-2 text-sm font-semibold text-black dark:text-white">
                            {label('wms.panel.block_positions')}
                        </h3>
                        {rackLoading ? (
                            <Skeleton className="h-24 w-full" />
                        ) : rackError ? (
                            <p className="text-sm text-danger-600 dark:text-danger-400">{rackError}</p>
                        ) : blockRows.length === 0 ? (
                            <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.cascade.empty')}</p>
                        ) : (
                            <>
                                {/* Visualização frontal (25/08): a torre do bloco, andar por
                                    andar — é aqui que "1 de 5 bloqueadas" fica visível de
                                    verdade, e não some atrás da cor dominante do mapa.
                                    `onPick` liga o modo ESCOLHA da torre: clica-se direto no
                                    vão desenhado. Sem ele a torre era só figura, o único
                                    caminho para a posição era a lista abaixo, e clicar no
                                    desenho sem resposta parecia tela travada.
                                    Com a torre clicável, listar as MESMAS posições embaixo
                                    virou eco — a lista só aparece para o que a torre não
                                    consegue desenhar (posição sem andar), que senão sumiria
                                    da tela sem deixar rastro. */}
                                <BlockTower
                                    rack={target.rack}
                                    bay={target.bay}
                                    positions={towerRows}
                                    onPick={onSelectLocation}
                                    label={label}
                                />
                                {offTowerRows.length > 0 ? (
                                    <ul className="mt-3 divide-y divide-mono-100 dark:divide-mono-900">
                                        {offTowerRows.map((row) => (
                                            <li key={row.id || row.code}>
                                                <RowButton row={row} label={label} onSelect={onSelectLocation} />
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </>
                        )}
                    </section>
                ) : null}
            </div>
        </aside>
    );
};

export default LocationInfoPanel;
